import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { Icon } from 'assets/icons';
import { Badge } from 'app/shared/badge';
import { toDateString } from 'app/shared/utils/date';
import { toReadableNumber } from 'app/shared/utils/number';
import styles from './invoices.module.css';

const statusBadge = (status) => {
  if (status === 'paid') return <Badge type="green" text="paid" />;
  if (status === 'unpaid') return <Badge type="red" text="unpaid" />;
  return <Badge text={status || 'pending'} />;
};

const InvoicesSkeleton = () => (
  <div className={styles.list}>
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className={styles.row}>
        <div className={styles.rowMain}>
          <Skeleton width={160} height={14} />
          <Skeleton width={110} height={12} style={{ marginTop: 6 }} />
        </div>
        <Skeleton width={70} height={20} borderRadius={20} />
      </div>
    ))}
  </div>
);

const invoiceDate = (invoice) =>
  new Date(
    invoice.created_on ||
      invoice.created_at ||
      invoice.metadata?.created_on ||
      invoice.due_date ||
      invoice.metadata?.due_date ||
      0
  ).getTime();

export const InvoicesList = ({ items = [], loading = false }) => {
  const download = (link) => {
    if (link) window.open(link, '_blank', 'noopener,noreferrer');
  };

  // Newest first, regardless of the order the records arrive in.
  const sorted = [...items].sort((a, b) => invoiceDate(b) - invoiceDate(a));

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <span className={styles.title}>Invoices</span>
        {!loading && items.length > 0 && <span className={styles.count}>{items.length}</span>}
      </div>

      {loading ? (
        <InvoicesSkeleton />
      ) : items.length === 0 ? (
        <div className={styles.empty}>
          <p className={styles.emptyTitle}>No invoices yet</p>
          <p className={styles.emptyBody}>Invoices you raise for clients will show up here.</p>
        </div>
      ) : (
        <div className={styles.list}>
          {sorted.map((invoice) => {
            const amount = invoice.total ?? invoice.cost;
            const link = invoice.pdf_link || invoice.location;
            return (
              <div key={invoice.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.client}>
                    {invoice.metadata?.tenant?.name || 'Client'}
                  </span>
                  <span className={styles.meta}>
                    {[
                      invoice.currency && amount != null
                        ? `${invoice.currency} ${toReadableNumber(amount)}`
                        : null,
                      toDateString(invoice.created_on || invoice.due_date)
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </div>
                <div className={styles.rowEnd}>
                  {statusBadge(invoice.status)}
                  {link && (
                    <button
                      className={styles.download}
                      onClick={() => download(link)}
                      aria-label="Download invoice"
                    >
                      <Icon name="download" width={16} height={16} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
